import type { IframePoolOrder } from '../../api';

/**
 * Sleek buys with `open_immediately`: the backend opens every card at purchase
 * (partner call 2026-10-02 — no unopened packs left behind), yet the player
 * still rips them one by one. Until its ceremony runs, such a card is shown as
 * sealed: its real `opened_at` waits here and the reveal reads it instead of
 * calling `mark_as_opened`.
 *
 * Module memory on purpose: a reload drops it and the order then shows as
 * already opened, which is exactly what it is.
 */
const heldOpenedAt = new Map<string, Map<string, string>>();

/** Right after a sleek purchase. A card the backend failed to open has no
 *  `openedAt` and is skipped: it takes the regular `mark_as_opened` path. */
export function holdPreOpenedCards(order: IframePoolOrder): void {
  const held = new Map<string, string>();
  for (const item of order.poolItems) {
    if (item.openedAt) held.set(String(item.id), item.openedAt);
  }
  if (held.size > 0) heldOpenedAt.set(String(order.id), held);
}

/** Shows the cards still waiting for their ceremony as sealed. */
export function maskPreOpenedCards(order: IframePoolOrder): IframePoolOrder {
  const held = heldOpenedAt.get(String(order.id));
  if (!held) return order;
  return {
    ...order,
    poolItems: order.poolItems.map((item) => (held.has(String(item.id)) ? { ...item, openedAt: null } : item)),
  };
}

/** The ceremony (or Close and vault) reached the card: hands back its real
 *  `openedAt` once and stops masking it. Null for a card that is not held. */
export function releasePreOpenedCard(orderId: string, itemId: string): string | null {
  const held = heldOpenedAt.get(String(orderId));
  const openedAt = held?.get(String(itemId)) ?? null;
  if (!held || openedAt === null) return null;
  held.delete(String(itemId));
  if (held.size === 0) heldOpenedAt.delete(String(orderId));
  return openedAt;
}
